import { useState } from "react";
import { motion } from "framer-motion";
import { useProduct } from "@/components/pdp/PdpDataContext";

// PDP3.2-only editorial overview: high-res lifestyle image + a soft blue-sage
// claim panel (copy verbatim from the approved mockup). Two switchable
// layouts — click the image to toggle:
//   • Version 1 — full-bleed, edge-to-edge two columns.
//   • Version 2 — contained to the page grid (max-w-[1600px] + gutter), with a
//     gap between the image and the panel like the rest of the page.
const IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/7s6bpx1h_Rectangle%2056.png";

const TEAL = "var(--brand-dark, #003646)";
const TEAL_20 = `color-mix(in srgb, ${TEAL} 20%, transparent)`;
const TEAL_70 = `color-mix(in srgb, ${TEAL} 70%, transparent)`;

// PDP3.3 — mobile shows the flat-cream contained portrait; desktop uses the
// first wide lifestyle image full-bleed with the copy overlaid on the right.
const BG_MOBILE_V33 =
  "https://static.prod-images.emergentagent.com/jobs/e3513a08-18b3-459d-901e-2457595c9150/images/d5e078e2c3be68418ee73e0707974ba4c05532782dd8bc8cb5a4a2846112512d.jpeg";
const BG_DESKTOP_V33 =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/2oaln4gd_Image%20Sep%2018%2C%202026%2C%2001_47_16%20AM.png";
const PANEL_V33 = "#ECE5DD";

const StoryImage = ({ onClick }) => (
  <div
    onClick={onClick}
    data-testid="product-story-image"
    aria-label="Switch layout"
    className="relative min-h-[440px] lg:min-h-[600px] overflow-hidden cursor-pointer"
  >
    <motion.img
      src={IMG}
      alt="Close-up of moisturizer being applied to skin"
      loading="lazy"
      initial={{ scale: 1.06, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0 w-full h-full object-cover"
    />
  </div>
);

const StoryPanel = () => (
  <div
    className="px-6 py-12 sm:px-10 lg:px-16 lg:py-20 flex flex-col justify-center"
    style={{ backgroundColor: "#D5E3E8", color: TEAL }}
  >
    <div className="max-w-2xl">
      <motion.h2
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="text-2xl sm:text-3xl lg:text-[2.6rem] font-bold uppercase tracking-tight leading-[1.12]"
      >
        Preventative retinol for all ages, and even tested safe for sensitive
        skin
      </motion.h2>

      <div className="h-px w-full my-8" style={{ backgroundColor: TEAL_20 }} />

      <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-2 sm:gap-8">
        <div
          className="font-mono text-[11px] tracking-[0.18em] uppercase pt-1"
          style={{ color: TEAL_70 }}
        >
          Details
        </div>
        <p className="text-[15px] lg:text-[17px] leading-relaxed">
          Our best-selling moisturizer to deliver immediate hydration and help
          defend against environmental stressors. This dermatologist-developed
          formula locks in moisture to support a healthy skin barrier without
          feeling heavy or greasy. Enriched with stabilized vitamin C,
          hinokitiol (beta-t), and vitamin E to visibly soften fine lines, even
          skin tone, and improve overall skin comfort.
        </p>
      </div>

      <div className="h-px w-full my-8" style={{ backgroundColor: TEAL_20 }} />

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:gap-8 items-baseline">
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase">
          Which skin type is it good for?
        </div>
        <div className="text-[15px] lg:text-lg font-medium whitespace-nowrap">
          Normal &nbsp;·&nbsp; Dry &nbsp;·&nbsp; Combination
        </div>
      </div>
    </div>
  </div>
);

const StoryCopyV33 = () => (
  <>
    <motion.h2
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="text-2xl sm:text-3xl lg:text-[2.6rem] font-bold uppercase tracking-tight leading-[1.12]"
    >
      Preventative retinol for all ages, and even tested safe for sensitive skin
    </motion.h2>

    <div className="h-px w-full my-7 lg:my-8" style={{ backgroundColor: TEAL_20 }} />

    <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-2 sm:gap-8">
      <div className="font-mono text-[11px] tracking-[0.18em] uppercase pt-1" style={{ color: TEAL_70 }}>
        Details
      </div>
      <p className="text-[15px] lg:text-[17px] leading-relaxed">
        Our best-selling moisturizer to deliver immediate hydration and help
        defend against environmental stressors. This dermatologist-developed
        formula locks in moisture to support a healthy skin barrier without
        feeling heavy or greasy. Enriched with stabilized vitamin C, hinokitiol
        (beta-t), and vitamin E to visibly soften fine lines, even skin tone,
        and improve overall skin comfort.
      </p>
    </div>

    <div className="h-px w-full my-7 lg:my-8" style={{ backgroundColor: TEAL_20 }} />

    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:gap-8 items-baseline">
      <div className="font-mono text-[11px] tracking-[0.18em] uppercase">
        Which skin type is it good for?
      </div>
      <div className="text-[15px] lg:text-lg font-medium whitespace-nowrap">
        Normal &nbsp;·&nbsp; Dry &nbsp;·&nbsp; Combination
      </div>
    </div>
  </>
);

const StoryBannerV33 = () => {
  const r = useProduct().mediaRadius || "20px";
  return (
  <section className="w-full bg-white pt-0 pb-14 lg:py-24" data-testid="product-story-v32" data-story-version="v33">
    {/* MOBILE — full-bleed image flush to top, copy below on cream */}
    <div className="lg:hidden" style={{ backgroundColor: PANEL_V33 }} data-testid="product-story-image">
      <div className="relative w-full h-[400px]">
        <img
          src={BG_MOBILE_V33}
          alt="Model applying moisturizer"
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover object-[center_top]"
        />
      </div>
      <div className="px-6 sm:px-10 py-10" style={{ color: TEAL }}>
        <StoryCopyV33 />
      </div>
    </div>

    {/* DESKTOP — full-bleed lifestyle image, copy overlaid on the right ~48% */}
    <div className="hidden lg:block max-w-[1600px] mx-auto px-8">
      <div
        className="relative overflow-hidden min-h-[640px] flex items-center"
        style={{ backgroundColor: "#EDE7DF", borderRadius: r }}
      >
        <img
          src={BG_DESKTOP_V33}
          alt="Model applying moisturizer"
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover object-[20%_center]"
        />
        <div className="relative z-10 w-full flex justify-end">
          <div className="w-[48%] pr-16 pl-6 py-16" style={{ color: TEAL }}>
            <StoryCopyV33 />
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

export const ProductStoryV32 = () => {
  const { v33 } = useProduct();
  const [version, setVersion] = useState(1);
  const toggle = () => setVersion((v) => (v === 1 ? 2 : 1));

  // PDP3.3 — single full-background editorial banner (per approved mockup):
  // the lifestyle image spans the whole block (rounded 20px) and the claim
  // copy sits over the light right side in teal. Scoped to v33 only.
  if (v33) return <StoryBannerV33 />;

  if (version === 1) {
    return (
      <section className="w-full bg-white" data-testid="product-story-v32" data-story-version="1">
        <div className="grid lg:grid-cols-2 items-stretch">
          <StoryImage onClick={toggle} />
          <StoryPanel />
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-white py-14 lg:py-24" data-testid="product-story-v32" data-story-version="2">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-stretch">
          <StoryImage onClick={toggle} />
          <StoryPanel />
        </div>
      </div>
    </section>
  );
};

export default ProductStoryV32;
